import { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Box, Grid } from "@material-ui/core";
import { FieldSetBox } from "../../fieldSetBox";
import { MapLegend } from "../../mapComponents";
import { StatementOfLimitingConditions } from "../stateOfLimitingConditions";
import { ValueReconciliationBox } from "../valueReconciliationBox";
import { paginateComments, PaginationResult } from "./valueReconciliationPagination";

export interface ValueReconciliationLayout {
  //true when the map page can hold the value reconciliation and the statement too (the usual layout)
  inlineFits: boolean;
  //the pages for the value reconciliation when it does not fit in the map page
  pagination: PaginationResult | null;
}

interface ValueReconciliationProbesProps {
  valuationValues: any;
  pageClassName: string;
  //only the reports with few comparables can share the map page, the others always get their own pages
  canShareMapPage: boolean;
  showLegend: boolean;
  legendMarkers: any[];
  onMeasured: (layout: ValueReconciliationLayout) => void;
}

const MAP_HEIGHT = 450;

//Hidden copies of the report pages, used only to measure with the real fonts and widths how much text fits in a page.
//They are rendered outside the report (in the body) so they are never part of the generated PDF.
export const ValueReconciliationProbes = ({ valuationValues, pageClassName, canShareMapPage, showLegend, legendMarkers, onMeasured }: ValueReconciliationProbesProps) => {
  const inlinePage = useRef<HTMLDivElement>(null);
  const inlineGrid = useRef<HTMLDivElement>(null);
  const inlineText = useRef<HTMLElement>(null);

  const firstPage = useRef<HTMLDivElement>(null);
  const firstGrid = useRef<HTMLDivElement>(null);
  const firstText = useRef<HTMLElement>(null);
  const firstStatement = useRef<HTMLDivElement>(null);

  const nextPage = useRef<HTMLDivElement>(null);
  const nextGrid = useRef<HTMLDivElement>(null);
  const nextText = useRef<HTMLElement>(null);
  const nextStatement = useRef<HTMLDivElement>(null);

  const comments: string = valuationValues.valueReconciliationComments || "";

  useLayoutEffect(() => {
    let cancelled = false;

    const contentFits = (page: HTMLDivElement | null, grid: HTMLDivElement | null) => {
      if (!page || !grid) {
        return true;
      }
      const pageTop = page.getBoundingClientRect().top;
      const paddingBottom = parseFloat(getComputedStyle(page).paddingBottom) || 0;
      const contentBottom = pageTop + page.clientTop + page.clientHeight - paddingBottom;
      return grid.getBoundingClientRect().bottom <= contentBottom + 0.5;
    };

    const measure = () => {
      if (cancelled) {
        return;
      }
      try {
        if (canShareMapPage) {
          if (inlineText.current) {
            inlineText.current.textContent = comments || "-";
          }
          if (contentFits(inlinePage.current, inlineGrid.current)) {
            onMeasured({ inlineFits: true, pagination: null });
            return;
          }
        }

        const pages = [
          { page: firstPage, grid: firstGrid, text: firstText, statement: firstStatement },
          { page: nextPage, grid: nextGrid, text: nextText, statement: nextStatement },
        ];
        const pagination = paginateComments(comments, {
          fits: (pageIndex, text, withStatement) => {
            const probe = pages[pageIndex === 0 ? 0 : 1];
            if (probe.text.current) {
              probe.text.current.textContent = text || "-";
            }
            if (probe.statement.current) {
              probe.statement.current.style.display = withStatement ? "" : "none";
            }
            return contentFits(probe.page.current, probe.grid.current);
          },
        });
        onMeasured({ inlineFits: false, pagination });
      } catch (error) {
        console.error("Could not measure the value reconciliation pages", error);
        //same layout as before the pagination: everything in one page that grows with the text
        onMeasured({
          inlineFits: canShareMapPage,
          pagination: { chunks: [comments], statementOnItsOwnPage: false },
        });
      }
    };

    //the measures depend on the fonts, so they wait until they are loaded
    const fonts = (document as any).fonts;
    if (fonts && fonts.ready) {
      fonts.ready.then(measure).catch(measure);
    } else {
      measure();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return createPortal(
    <div style={{ position: "fixed", top: 0, left: -99999, visibility: "hidden", pointerEvents: "none" }}>
      {canShareMapPage && (
        <div ref={inlinePage} className={pageClassName}>
          <Grid container direction="column" spacing={2} ref={inlineGrid}>
            <Grid item>
              <FieldSetBox title="Comparable">
                <Box style={{ height: MAP_HEIGHT, width: "100%" }} />
              </FieldSetBox>
            </Grid>
            {showLegend && (
              <Grid item>
                <FieldSetBox title="Legend">
                  <MapLegend markers={legendMarkers} />
                </FieldSetBox>
              </Grid>
            )}
            <Grid item xs={12}>
              <ValueReconciliationBox valuationValues={valuationValues} comments="" commentsRef={inlineText} />
            </Grid>
            <Grid item xs={12}>
              <StatementOfLimitingConditions />
            </Grid>
          </Grid>
        </div>
      )}
      <div ref={firstPage} className={pageClassName}>
        <Grid container direction="column" spacing={2} ref={firstGrid}>
          <Grid item xs={12}>
            <ValueReconciliationBox valuationValues={valuationValues} comments="" commentsRef={firstText} />
          </Grid>
          <Grid item xs={12} ref={firstStatement}>
            <StatementOfLimitingConditions />
          </Grid>
        </Grid>
      </div>
      <div ref={nextPage} className={pageClassName}>
        <Grid container direction="column" spacing={2} ref={nextGrid}>
          <Grid item xs={12}>
            <ValueReconciliationBox valuationValues={valuationValues} title="Value Reconciliation (continued)" showValues={false} comments="" commentsRef={nextText} />
          </Grid>
          <Grid item xs={12} ref={nextStatement}>
            <StatementOfLimitingConditions />
          </Grid>
        </Grid>
      </div>
    </div>,
    document.body
  );
};
